import { useState } from "react";
import api from "../api/api";
import {
    Mail,
    Phone,
    User,
    MessageSquare,
    FileText,
} from "lucide-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setError("");
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name || !form.email || !form.phone || !form.message) {
            setError("Please fill all required fields");
            return;
        }

        setLoading(true);

        try {
            if (!executeRecaptcha) {
                setError("reCAPTCHA not loaded. Please refresh page.");
                setLoading(false);
                return;
            }

            const token = await executeRecaptcha("contact_form");

            const res = await api.post("/contact", {
                ...form,
                recaptcha_token: token,
            });

            if (res.data.success) {
                setSuccess("Message sent successfully!");
                setForm({
                    name: "",
                    email: "",
                    phone: "",
                    subject: "",
                    message: "",
                });
            } else {
                setError(res.data.message || "Failed to send message");
            }
        } catch (err) {
            setError(
                err.response?.data?.message ||
                "reCAPTCHA verification failed or server error."
            );
        } finally {
            setLoading(false);
        }
    };

    const inputClass = `
w-full
pl-12
pr-4
py-3.5
rounded-2xl
border
border-[#DDD2BA]
dark:border-gray-700
bg-[#FCFAF5]
dark:bg-gray-900
text-[#1F2937]
dark:text-white
placeholder:text-gray-400
outline-none
transition-all
duration-300
focus:border-[#B89442]
focus:ring-4
focus:ring-[#B89442]/20
`;

    const iconClass =
        "absolute left-4 top-1/2 -translate-y-1/2 text-[#0F766E]";

    return (
        <div className="w-full">

            {success && (
                <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">
                    {success}
                </div>
            )}

            {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">

                {/* Name */}
                <div className="relative">
                    <User className={iconClass} size={18} />
                    <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your Name"
                        className={inputClass}
                    />
                </div>

                {/* Email */}
                <div className="relative">
                    <Mail className={iconClass} size={18} />
                    <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="Your Email"
                        className={inputClass}
                    />
                </div>

                {/* Phone */}
                <div className="relative">
                    <Phone className={iconClass} size={18} />
                    <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="Your Phone"
                        className={inputClass}
                    />
                </div>

                {/* Subject */}
                <div className="relative">
                    <FileText className={iconClass} size={18} />
                    <input
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        placeholder="Subject"
                        className={inputClass}
                    />
                </div>

                {/* Message */}
                <div className="relative">
                    <MessageSquare
                        className="absolute left-4 top-4 text-[#0F766E]"
                        size={18}
                    />
                    <textarea
                        rows="5"
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Write your message..."
                        className="
w-full
pl-12
pr-4
py-3.5
rounded-2xl
border
border-[#DDD2BA]
dark:border-gray-700
bg-[#FCFAF5]
dark:bg-gray-900
text-[#1F2937]
dark:text-white
placeholder:text-gray-400
resize-none
outline-none
transition-all
duration-300
focus:border-[#B89442]
focus:ring-4
focus:ring-[#B89442]/20
"
                    />
                </div>

                {/* BUTTON */}
                <button
                    type="submit"
                    disabled={loading}
                    className="
        w-full
        rounded-2xl
        bg-gradient-to-r
        from-[#B89442]
        to-[#D4B76A]
        py-4
        text-white
        font-semibold
        tracking-wide
        shadow-lg
        transition-all
        duration-300
        hover:-translate-y-1
        hover:shadow-2xl
        hover:from-[#0F766E]
        hover:to-[#0F766E]
        disabled:opacity-60
        disabled:cursor-not-allowed
    "
                >
                    {loading ? "Sending Message..." : "Send Message →"}
                </button>
            </form>
        </div>
    );
}